import { ref, computed } from 'vue';
import gql from 'graphql-tag';
import { useQuery } from '@vue/apollo-composable';
import permissionUtils from '@utils/permission.utils';
import { PermissionsDict } from '@models/account/useAccountInfo';

export enum PermissionsStatus {
    OFF = -1, //功能下线，所有人都不会看到
    TEST = 0, //灰度上线，有对应权限才能看到
    ON = 1 //功能上线，所有人都可以看到
}

export interface OriginPermission {
    sid?: string;
    permissionDict: PermissionsDict[];
    permissionIdList: string[];
}

const GET_PERMISSION_QUERY = gql`
    query getProfilePermission {
        getProfilePermission {
            permissionDict {
                updateTime
                description
                id
                name
                status
            }
            permissionIdList
        }
    }
`;

const permissions = ref<OriginPermission | undefined>();

const hasExperienceV4Permission = computed(() => {
    return checkPermission('experienceV4');
});

const hasExperienceAdvancePermission = computed(() => {
    return checkPermission('enableAdvance');
});

const hasHomePermission = computed(() => {
    return checkPermission('enableHomeV2');
});

const hasIntegrationPermission = computed(() => {
    return checkPermission('enableIntegration');
});

const hasIntegrationShopifyPermission = computed(() => {
    return checkPermission('enableIntegrationShopify');
});

const hasIntegrationApiPermission = computed(() => {
    return checkPermission('enableIntegrationApi');
});

const hasHeatMapReportPermission = computed(() => {
    return checkPermission('heatMapReport');
});

const hasExperienceBanditPermission = computed(() => {
    return checkPermission('engageVariant');
});

const hasExperienceListPagination = computed(() => {
    return checkPermission('fasterEngageList');
});

export function usePermission() {
    const getPermission = (
        sid?: string,
        resultCallback?: (T: OriginPermission) => void,
        rejectCallback?: (err: any) => void
    ) => {
        if (sid && permissions.value?.sid === sid) {
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(permissions.value);
            return;
        }

        const { onResult, onError } = useQuery<{ getProfilePermission: OriginPermission }>(
            GET_PERMISSION_QUERY,
            {},
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            if (data.data?.getProfilePermission) {
                permissions.value = data.data?.getProfilePermission;
                permissions.value.sid = sid;

                // 执行回调
                resultCallback &&
                    typeof resultCallback === 'function' &&
                    resultCallback(permissions.value);
            }
        });

        onError(err => {
            permissions.value = undefined;
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    return {
        permissions,
        hasExperienceV4Permission,
        hasExperienceAdvancePermission,
        hasHomePermission,
        hasIntegrationPermission,
        hasIntegrationShopifyPermission,
        hasIntegrationApiPermission,
        hasHeatMapReportPermission,
        hasExperienceBanditPermission,
        hasExperienceListPagination,
        getPermission
    };
}

export function checkPermission(permissionName: string) {
    if (!permissions.value) {
        return false;
    }
    return permissionUtils.includePermissionWithAccountInfo([permissionName], permissions.value);
}
